import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Search, User, FolderKanban, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../lib/api';

interface SearchResult {
  type: 'project' | 'task';
  id: string;
  name: string;
  projectId?: string;
  sheetId?: string;
}

export function Header() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const doSearch = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const [projectsRes, tasksRes] = await Promise.allSettled([
        api.get(`/projects?search=${encodeURIComponent(query)}&limit=5`),
        api.get(`/tasks/search?query=${encodeURIComponent(query)}&limit=5`),
      ]);

      const results: SearchResult[] = [];

      if (projectsRes.status === 'fulfilled') {
        const raw = projectsRes.value.data as any;
        const projects = raw?.data || raw?.projects || (Array.isArray(raw) ? raw : []);
        if (Array.isArray(projects)) {
          projects.slice(0, 5).forEach((p: any) => {
            results.push({ type: 'project', id: p.id, name: p.name });
          });
        }
      }

      // Tasks search might not have a dedicated endpoint — skip if it fails
      if (tasksRes.status === 'fulfilled') {
        const raw = tasksRes.value.data as any;
        const tasks = raw?.data || raw?.tasks || (Array.isArray(raw) ? raw : []);
        if (Array.isArray(tasks)) {
          tasks.slice(0, 5).forEach((t: any) => {
            results.push({ type: 'task', id: t.id, name: t.name, projectId: t.projectId, sheetId: t.sheetId });
          });
        }
      }

      setSearchResults(results);
      setShowResults(results.length > 0);
    } catch {
      setSearchResults([]);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (searchQuery.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    debounceRef.current = setTimeout(() => doSearch(searchQuery), 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchQuery, doSearch]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleResultClick = (result: SearchResult) => {
    setShowResults(false);
    setSearchQuery('');
    if (result.type === 'project') {
      navigate(`/projects/${result.id}`);
    } else if (result.sheetId && result.projectId) {
      navigate(`/projects/${result.projectId}/sheets/${result.sheetId}`);
    }
  };

  const getInitials = () => {
    if (!user) return 'U';
    const firstName = (user.firstName || '').trim();
    const lastName = (user.lastName || '').trim();
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (firstName) return firstName.charAt(0).toUpperCase();
    if (lastName) return lastName.charAt(0).toUpperCase();
    if (user.email) return user.email.charAt(0).toUpperCase();
    return 'U';
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b bg-background px-6">
      {/* Search */}
      <div className="flex flex-1 items-center gap-4">
        <div ref={searchRef} className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search projects, tasks..."
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => searchResults.length > 0 && setShowResults(true)}
          />
          {showResults && searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-50 overflow-hidden">
              {searchResults.map((result) => (
                <button
                  key={`${result.type}-${result.id}`}
                  className="flex items-center gap-3 w-full px-3 py-2 text-sm text-left hover:bg-gray-100 transition-colors"
                  onClick={() => handleResultClick(result)}
                >
                  {result.type === 'project' ? (
                    <FolderKanban className="h-4 w-4 text-indigo-500 flex-shrink-0" />
                  ) : (
                    <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="truncate font-medium">{result.name}</div>
                    <div className="text-xs text-gray-400">{result.type === 'project' ? 'Project' : 'Task'}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        {/* Notifications */}
        <Button variant="ghost" size="icon">
          <Bell className="h-5 w-5" />
        </Button>

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-10 w-10 rounded-full">
              <Avatar>
                <AvatarImage src={user?.profilePicture} alt={user?.email} />
                <AvatarFallback>{getInitials()}</AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs leading-none text-muted-foreground">
                  {user?.email}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem>
              <User className="mr-2 h-4 w-4" />
              <span>Profile</span>
            </DropdownMenuItem>
            <DropdownMenuItem>
              <Search className="mr-2 h-4 w-4" />
              <span>Settings</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => signOut()}>
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
